import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Helper function to extract subdomain from hostname
const getSubdomainFromHostname = () => {
  const hostname = window.location.hostname;
  
  // Development environments
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('emergentagent.com')) {
    return null; // Will use default (TRB)
  }
  
  // Production: trb.surdurulebilirdenetim.com or royalcert.surdurulebilirdenetim.com
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    // Skip common non-tenant subdomains
    if (['www', 'api', 'portal', 'app'].includes(subdomain)) {
      return subdomain === 'portal' ? 'portal' : null;
    }
    return subdomain;
  }
  
  return null;
};

export const TenantProvider = ({ children }) => {
  // Initialize tenant from subdomain immediately (no localStorage)
  const subdomain = getSubdomainFromHostname();
  
  const getInitialTenant = () => {
    // Set initial tenant based on subdomain to prevent flash
    if (subdomain === 'royalcert') {
      return {
        tenant_id: 'tenant_royalcert_default',
        slug: 'royalcert',
        name: 'RoyalCert',
        logo_url: '/royalcert_logo.png',
        primary_color: '#b91c1c',
        ncr_form_type: 'single'
      };
    }
    // Default to TRB
    return {
      tenant_id: 'tenant_trb_default',
      slug: 'trb',
      name: 'TRB INTERNATIONAL CERTIFICATION',
      logo_url: '/logo.png',
      primary_color: '#16a34a',
      ncr_form_type: 'multiple'
    };
  };

  const [tenant, setTenant] = useState(getInitialTenant);
  const [isSuperAdminView, setIsSuperAdminView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        console.log('Detected subdomain:', subdomain);
        
        // If portal subdomain, set super admin view
        if (subdomain === 'portal') {
          setIsSuperAdminView(true);
          setLoading(false);
          return;
        }
        
        // If we have a subdomain (trb, royalcert), fetch that tenant directly
        if (subdomain && subdomain !== 'portal') {
          try {
            const tenantResponse = await fetch(`${API_URL}/api/tenants/by-slug/${subdomain}`, {
              credentials: 'include'
            });
            if (tenantResponse.ok) {
              const tenantData = await tenantResponse.json();
              console.log('Fetched tenant by slug:', tenantData);
              setTenant(tenantData);
              
              // Store tenant in localStorage for API calls
              localStorage.setItem('currentTenantId', tenantData.tenant_id);
              localStorage.setItem('currentTenantSlug', tenantData.slug);
              
              setLoading(false);
              return;
            }
          } catch (err) {
            console.error('Error fetching tenant by slug:', err);
          }
        }
        
        // Fallback: Try current-tenant API
        const response = await fetch(`${API_URL}/api/current-tenant`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.tenant) {
            setTenant(data.tenant);
            localStorage.setItem('currentTenantId', data.tenant.tenant_id);
            localStorage.setItem('currentTenantSlug', data.tenant.slug);
          }
          setIsSuperAdminView(data.is_super_admin_view);
        }
      } catch (err) {
        console.error('Error fetching tenant:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Clear localStorage if subdomain changed
    const storedSlug = localStorage.getItem('currentTenantSlug');
    if (subdomain && storedSlug && storedSlug !== subdomain) {
      localStorage.removeItem('currentTenantId');
      localStorage.removeItem('currentTenantSlug');
    }
    
    // Set localStorage immediately based on subdomain
    if (subdomain && subdomain !== 'portal') {
      const initialTenant = getInitialTenant();
      localStorage.setItem('currentTenantId', initialTenant.tenant_id);
      localStorage.setItem('currentTenantSlug', initialTenant.slug);
    }

    fetchTenant();
  }, [subdomain]);

  // Update document title when tenant changes
  useEffect(() => {
    if (tenant?.name) {
      document.title = `${tenant.name} - Denetim Yönetimi`;
    } else if (subdomain === 'royalcert') {
      document.title = 'RoyalCert - Denetim Yönetimi';
    } else if (subdomain === 'trb') {
      document.title = 'TRB - Denetim Yönetimi';
    } else {
      document.title = 'Sürdürülebilir Turizm Denetim Yönetimi';
    }
  }, [tenant, subdomain]);

  // Get tenant-specific logo
  const getTenantLogo = () => {
    if (tenant?.logo_url) {
      return tenant.logo_url;
    }
    return '/logo.png';
  };

  // Get tenant-specific name
  const getTenantName = () => {
    if (tenant?.name) {
      return tenant.name;
    }
    return 'TRB INTERNATIONAL CERTIFICATION';
  };

  // Get tenant-specific primary color
  const getPrimaryColor = () => {
    if (tenant?.primary_color) {
      return tenant.primary_color;
    }
    return '#16a34a';
  };

  // Check if NCR form is single (RoyalCert style) or multiple (TRB style)
  const isNcrFormSingle = () => {
    return tenant?.ncr_form_type === 'single';
  };

  const value = {
    tenant,
    setTenant,
    isSuperAdminView,
    loading,
    error,
    getTenantLogo,
    getTenantName,
    getPrimaryColor,
    isNcrFormSingle,
    tenantId: tenant?.tenant_id,
    tenantSlug: tenant?.slug
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
